import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfYear, isWithinInterval, differenceInDays } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import {
  TrendingUp,
  Trophy,
  Target,
  Zap,
  Star,
  Flame,
  Crown,
  Rocket,
  Gift,
  ShoppingBag,
  DollarSign,
  ArrowUpRight,
  Sparkles,
  Medal,
  CheckCircle2,
  CalendarIcon,
  Calendar as CalendarRange,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowDownRight,
  GitCompare,
  Filter,
  X,
  Users,
} from "lucide-react";
import { formatCurrency, getCurrencySymbol, type Currency } from "@/lib/currency";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from "recharts";

type DateRange = {
  from: Date;
  to: Date;
};

type PresetRange = "7d" | "30d" | "this_month" | "last_month" | "this_quarter" | "this_year" | "custom";

interface Participant {
  id: string;
  name: string;
  has_paid: boolean;
}

interface SplitPaymentRequest {
  id: string;
  product_name: string;
  total_price: number;
  split_amount: number;
  deadline: string;
  status: string;
  created_at: string;
  completed_at?: string;
  fulfillment_status?: string | null;
  participants: Participant[];
}

interface ProfessionalTerminology {
  orders?: string;
  products?: string;
  customers?: string;
  store?: string;
}

interface MerchantAnalyticsProps {
  requests: SplitPaymentRequest[];
  splitsThisMonth: number;
  splitLimit: number | null;
  currency?: Currency;
  isProfessional?: boolean;
  terminology?: ProfessionalTerminology;
  onNavigateToOrders?: (filter?: string) => void;
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  unlocked: boolean;
  progress: number;
  target: number;
  tier: "bronze" | "silver" | "gold" | "platinum";
}

interface ProductPerformance {
  name: string;
  splits: number;
  revenue: number;
  conversionRate: number;
}

const TIER_COLORS = {
  bronze: "from-amber-600 to-amber-800",
  silver: "from-slate-400 to-slate-600",
  gold: "from-yellow-400 to-yellow-600",
  platinum: "from-purple-400 to-purple-600",
};

const TIER_BG = {
  bronze: "bg-amber-500/10 border-amber-500/30",
  silver: "bg-slate-400/10 border-slate-400/30",
  gold: "bg-yellow-400/10 border-yellow-400/30",
  platinum: "bg-purple-400/10 border-purple-400/30",
};

const PIE_COLORS = ["hsl(var(--primary))", "hsl(var(--muted))", "hsl(var(--destructive))"];

const PRESET_LABELS: Record<PresetRange, string> = {
  "7d": "Last 7 Days",
  "30d": "Last 30 Days",
  "this_month": "This Month",
  "last_month": "Last Month",
  "this_quarter": "This Quarter",
  "this_year": "This Year",
  "custom": "Custom Range",
};

function getPresetDateRange(preset: PresetRange): DateRange {
  const now = new Date();
  switch (preset) {
    case "7d":
      return { from: subDays(now, 7), to: now };
    case "30d":
      return { from: subDays(now, 30), to: now };
    case "this_month":
      return { from: startOfMonth(now), to: now };
    case "last_month":
      const lastMonth = subMonths(now, 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    case "this_quarter":
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const quarterStart = new Date(now.getFullYear(), currentQuarter * 3, 1);
      return { from: quarterStart, to: now };
    case "this_year":
      return { from: startOfYear(now), to: now };
    case "custom":
    default:
      return { from: startOfMonth(now), to: now };
  }
}

export function MerchantAnalytics({ requests, splitsThisMonth, splitLimit, currency = 'INR', isProfessional = false, terminology = {}, onNavigateToOrders }: MerchantAnalyticsProps) {
  const currencySymbol = getCurrencySymbol(currency);
  // Get dynamic terminology with fallbacks
  const customersLabel = terminology?.customers || (isProfessional ? "clients" : "participants");
  const productsLabel = terminology?.products || (isProfessional ? "Services" : "Products");
  const productLabel = terminology?.products || (isProfessional ? "Service" : "Product");
  
  // Professional-friendly terminology
  const labels = isProfessional ? {
    revenue: "Total Earnings",
    conversionRate: "Collection Rate",
    completedSplits: "Completed Payments",
    paymentStreak: "Collection Streak",
    split: "payment",
    splits: "payments",
    participants: customersLabel,
    products: productsLabel,
    product: productLabel,
    avgSplitValue: "Avg. Payment Value",
    topProducts: `Top Performing ${productsLabel}`,
    topProductsDesc: `${productsLabel} generating the most revenue`,
    revenueVia: "collected",
    splitStatus: "Payment Status",
    splitStatusDesc: "Distribution of all payments",
    noSplits: "No payments yet",
    noProductData: `No ${productLabel.toLowerCase()} data yet`,
    startCreating: "Start creating collections to see performance",
  } : {
    revenue: "Total Revenue",
    conversionRate: "Conversion Rate",
    completedSplits: "Completed Splits",
    paymentStreak: "Payment Streak",
    split: "split",
    splits: "splits",
    participants: "participants",
    products: "Products",
    product: "Product",
    avgSplitValue: "Avg. Split Value",
    topProducts: "Top Performing Products",
    topProductsDesc: "Products generating the most revenue via split payments",
    revenueVia: "via Shakatah",
    splitStatus: "Split Status",
    splitStatusDesc: "Distribution of all splits",
    noSplits: "No splits yet",
    noProductData: "No product data yet",
    startCreating: "Start creating splits to see performance",
  };
  const [selectedPreset, setSelectedPreset] = useState<PresetRange>("this_month");
  const [dateRange, setDateRange] = useState<DateRange>(getPresetDateRange("this_month"));
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string>("all");
  const [showComparison, setShowComparison] = useState(false);

  // Get unique product names for filter
  const productNames = useMemo(() => {
    const names = new Set<string>();
    requests.forEach(r => names.add(r.product_name));
    return Array.from(names).sort();
  }, [requests]);

  const handlePresetChange = (preset: PresetRange) => {
    setSelectedPreset(preset);
    if (preset !== "custom") {
      setDateRange(getPresetDateRange(preset));
    }
  };

  const handleCustomDateSelect = (date: Date | undefined, type: "from" | "to") => {
    if (!date) return;
    setSelectedPreset("custom");
    setDateRange(prev => ({
      ...prev,
      [type]: date,
    }));
  };

  // Calculate previous period for comparison
  const previousPeriod = useMemo(() => {
    const daysDiff = differenceInDays(dateRange.to, dateRange.from);
    const prevTo = new Date(dateRange.from);
    prevTo.setDate(prevTo.getDate() - 1);
    const prevFrom = new Date(prevTo);
    prevFrom.setDate(prevFrom.getDate() - daysDiff);
    return { from: prevFrom, to: prevTo };
  }, [dateRange]);

  // Filter requests by date range and product
  const filteredRequests = useMemo(() => {
    return requests.filter(r => {
      const createdAt = new Date(r.created_at);
      const inDateRange = isWithinInterval(createdAt, { start: dateRange.from, end: dateRange.to });
      const matchesProduct = selectedProduct === "all" || r.product_name === selectedProduct;
      return inDateRange && matchesProduct;
    });
  }, [requests, dateRange, selectedProduct]);

  // Filter requests for previous period (for comparison)
  const previousPeriodRequests = useMemo(() => {
    if (!showComparison) return [];
    return requests.filter(r => {
      const createdAt = new Date(r.created_at);
      const inDateRange = isWithinInterval(createdAt, { start: previousPeriod.from, end: previousPeriod.to });
      const matchesProduct = selectedProduct === "all" || r.product_name === selectedProduct;
      return inDateRange && matchesProduct;
    });
  }, [requests, previousPeriod, selectedProduct, showComparison]);

  // Calculate metrics for a given set of requests
  const calculateMetrics = (reqs: typeof requests) => {
    const now = new Date();
    const completedRequests = reqs.filter(r => r.status === "completed");
    const pendingRequests = reqs.filter(r => {
      const deadline = new Date(r.deadline);
      return r.status === "pending" && deadline > now;
    });
    const expiredRequests = reqs.filter(r => {
      const deadline = new Date(r.deadline);
      return r.status === "expired" || (r.status === "pending" && deadline <= now);
    });

    const totalRevenue = completedRequests.reduce((sum, r) => sum + Number(r.total_price), 0);
    const pendingRevenue = pendingRequests.reduce((sum, r) => {
      const unpaid = r.participants.filter(p => !p.has_paid).length;
      return sum + (unpaid * Number(r.split_amount));
    }, 0);
    const collectedRevenue = reqs.reduce((sum, r) => {
      const paid = r.participants.filter(p => p.has_paid).length;
      return sum + (paid * Number(r.split_amount));
    }, 0);

    const totalParticipants = reqs.reduce((sum, r) => sum + r.participants.length, 0);
    const paidParticipants = reqs.reduce((sum, r) => sum + r.participants.filter(p => p.has_paid).length, 0);
    const conversionRate = totalParticipants > 0 ? (paidParticipants / totalParticipants) * 100 : 0;
    const requestConversionRate = reqs.length > 0 ? (completedRequests.length / reqs.length) * 100 : 0;

    return {
      totalRequests: reqs.length,
      completedRequests: completedRequests.length,
      pendingRequests: pendingRequests.length,
      expiredRequests: expiredRequests.length,
      totalRevenue,
      pendingRevenue,
      collectedRevenue,
      conversionRate,
      requestConversionRate,
      totalParticipants,
      paidParticipants,
    };
  };

  // Calculate comparison percentages
  const getChangePercent = (current: number, previous: number): { value: number; isPositive: boolean } => {
    if (previous === 0) return { value: current > 0 ? 100 : 0, isPositive: current >= 0 };
    const change = ((current - previous) / previous) * 100;
    return { value: Math.abs(change), isPositive: change >= 0 };
  };

  // Previous period metrics for comparison
  const previousMetrics = useMemo(() => {
    if (!showComparison) return null;
    return calculateMetrics(previousPeriodRequests);
  }, [previousPeriodRequests, showComparison]);

  const analytics = useMemo(() => {
    const now = new Date();
    
    // Calculate core metrics using filtered requests
    const completedRequests = filteredRequests.filter(r => r.status === "completed");
    const pendingRequests = filteredRequests.filter(r => {
      const deadline = new Date(r.deadline);
      return r.status === "pending" && deadline > now;
    });
    const expiredRequests = filteredRequests.filter(r => {
      const deadline = new Date(r.deadline);
      return r.status === "expired" || (r.status === "pending" && deadline <= now);
    });

    // Revenue calculations
    const totalRevenue = completedRequests.reduce((sum, r) => sum + Number(r.total_price), 0);
    const pendingRevenue = pendingRequests.reduce((sum, r) => {
      const unpaid = r.participants.filter(p => !p.has_paid).length;
      return sum + (unpaid * Number(r.split_amount));
    }, 0);
    const collectedRevenue = filteredRequests.reduce((sum, r) => {
      const paid = r.participants.filter(p => p.has_paid).length;
      return sum + (paid * Number(r.split_amount));
    }, 0);

    // Conversion rate
    const totalParticipants = filteredRequests.reduce((sum, r) => sum + r.participants.length, 0);
    const paidParticipants = filteredRequests.reduce((sum, r) => sum + r.participants.filter(p => p.has_paid).length, 0);
    const conversionRate = totalParticipants > 0 ? (paidParticipants / totalParticipants) * 100 : 0;

    // Request conversion rate
    const requestConversionRate = filteredRequests.length > 0 
      ? (completedRequests.length / filteredRequests.length) * 100 
      : 0;

    // Product performance
    const productMap = new Map<string, { splits: number; revenue: number; paid: number; total: number }>();
    filteredRequests.forEach(r => {
      const existing = productMap.get(r.product_name) || { splits: 0, revenue: 0, paid: 0, total: 0 };
      const paidCount = r.participants.filter(p => p.has_paid).length;
      productMap.set(r.product_name, {
        splits: existing.splits + 1,
        revenue: existing.revenue + (r.status === "completed" ? Number(r.total_price) : paidCount * Number(r.split_amount)),
        paid: existing.paid + paidCount,
        total: existing.total + r.participants.length,
      });
    });

    const topProducts: ProductPerformance[] = Array.from(productMap.entries())
      .map(([name, data]) => ({
        name: name.length > 20 ? name.substring(0, 20) + "..." : name,
        splits: data.splits,
        revenue: data.revenue,
        conversionRate: data.total > 0 ? (data.paid / data.total) * 100 : 0,
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    // Monthly trend (last 6 months - uses ALL requests, not filtered by date range)
    const monthlyData: { month: string; splits: number; revenue: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      // Fix: Use day 1 to avoid month-skipping issues with setMonth()
      const now = new Date();
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
      
      // Use ALL requests for trend chart, not filtered ones
      const monthRequests = requests.filter(r => {
        const created = new Date(r.created_at);
        return created >= monthStart && created <= monthEnd;
      });

      const monthRevenue = monthRequests.reduce((sum, r) => {
        const paid = r.participants.filter(p => p.has_paid).length;
        return sum + (paid * Number(r.split_amount));
      }, 0);

      monthlyData.push({
        month: date.toLocaleString("default", { month: "short" }),
        splits: monthRequests.length,
        revenue: monthRevenue,
      });
    }

    // Status distribution for pie chart
    const statusData = [
      { name: "Completed", value: completedRequests.length, color: "hsl(var(--primary))" },
      { name: "Pending", value: pendingRequests.length, color: "hsl(var(--muted))" },
      { name: "Expired", value: expiredRequests.length, color: "hsl(var(--destructive))" },
    ].filter(d => d.value > 0);

    // Average split value
    const avgSplitValue = completedRequests.length > 0 
      ? totalRevenue / completedRequests.length 
      : 0;

    // Streak calculation (consecutive days with completed payments)
    let currentStreak = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 30; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() - i);
      const dayStart = new Date(checkDate);
      const dayEnd = new Date(checkDate);
      dayEnd.setDate(dayEnd.getDate() + 1);
      
      const hasPayment = requests.some(r => 
        r.participants.some(p => p.has_paid) && 
        new Date(r.created_at) >= dayStart && 
        new Date(r.created_at) < dayEnd
      );
      
      if (hasPayment) {
        currentStreak++;
      } else if (i > 0) {
        break;
      }
    }

    // Professional-specific metrics
    // Monthly Recurring Revenue (MRR) - sum of recurring payments this month
    const thisMonth = new Date();
    const thisMonthStart = new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 1);
    const thisMonthRequests = filteredRequests.filter(r => new Date(r.created_at) >= thisMonthStart);
    const mrr = thisMonthRequests.reduce((sum, r) => {
      const paid = r.participants.filter(p => p.has_paid).length;
      return sum + (paid * Number(r.split_amount));
    }, 0);

    // Client Retention Rate - % of unique clients who have paid in multiple requests
    const clientPayments = new Map<string, number>();
    requests.forEach(r => {
      r.participants.forEach(p => {
        if (p.has_paid && p.name) {
          clientPayments.set(p.name, (clientPayments.get(p.name) || 0) + 1);
        }
      });
    });
    const totalClients = clientPayments.size;
    const repeatClients = Array.from(clientPayments.values()).filter(count => count > 1).length;
    const clientRetentionRate = totalClients > 0 ? (repeatClients / totalClients) * 100 : 0;

    // Average Payment Delay - avg days between request creation and payment
    let totalDelayDays = 0;
    let paidRequestCount = 0;
    filteredRequests.forEach(r => {
      if (r.completed_at) {
        const createdAt = new Date(r.created_at);
        const completedAt = new Date(r.completed_at);
        const delayDays = Math.max(0, (completedAt.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));
        totalDelayDays += delayDays;
        paidRequestCount++;
      }
    });
    const avgPaymentDelay = paidRequestCount > 0 ? totalDelayDays / paidRequestCount : 0;

    return {
      totalRequests: filteredRequests.length,
      completedRequests: completedRequests.length,
      pendingRequests: pendingRequests.length,
      expiredRequests: expiredRequests.length,
      totalRevenue,
      pendingRevenue,
      collectedRevenue,
      conversionRate,
      requestConversionRate,
      topProducts,
      monthlyData,
      statusData,
      avgSplitValue,
      totalParticipants,
      paidParticipants,
      currentStreak,
      // Professional KPIs
      mrr,
      clientRetentionRate,
      avgPaymentDelay,
      totalClients,
      repeatClients,
    };
  }, [filteredRequests, requests]);

  // Generate achievements based on metrics - conditional for professional vs merchant
  const achievements: Achievement[] = useMemo(() => {
    const { totalRevenue, completedRequests, conversionRate, totalRequests, currentStreak, clientRetentionRate, totalClients, mrr } = analytics;

    if (isProfessional) {
      // Professional-specific achievements
      return [
        {
          id: "first_collection",
          title: "First Collection",
          description: "Complete your first payment collection",
          icon: <Rocket className="h-5 w-5" />,
          unlocked: completedRequests >= 1,
          progress: Math.min(completedRequests, 1),
          target: 1,
          tier: "bronze",
        },
        {
          id: "client_builder",
          title: "Client Builder",
          description: "Collect payments from 10 clients",
          icon: <Target className="h-5 w-5" />,
          unlocked: totalClients >= 10,
          progress: Math.min(totalClients, 10),
          target: 10,
          tier: "bronze",
        },
        {
          id: "client_champion",
          title: "Client Champion",
          description: "Build a base of 50 clients",
          icon: <Trophy className="h-5 w-5" />,
          unlocked: totalClients >= 50,
          progress: Math.min(totalClients, 50),
          target: 50,
          tier: "silver",
        },
        {
          id: "earnings_milestone",
          title: `${currencySymbol}10K Earner`,
          description: `Collect ${currencySymbol}10,000 in payments`,
          icon: <DollarSign className="h-5 w-5" />,
          unlocked: totalRevenue >= 10000,
          progress: Math.min(totalRevenue, 10000),
          target: 10000,
          tier: "gold",
        },
        {
          id: "earnings_pro",
          title: "Top Earner",
          description: `Collect ${currencySymbol}100,000 in payments`,
          icon: <Crown className="h-5 w-5" />,
          unlocked: totalRevenue >= 100000,
          progress: Math.min(totalRevenue, 100000),
          target: 100000,
          tier: "platinum",
        },
        {
          id: "retention_master",
          title: "Retention Master",
          description: "Achieve 50%+ client retention",
          icon: <Zap className="h-5 w-5" />,
          unlocked: clientRetentionRate >= 50 && totalClients >= 5,
          progress: Math.min(clientRetentionRate, 50),
          target: 50,
          tier: "silver",
        },
        {
          id: "collection_streak",
          title: "Collection Streak",
          description: "7-day collection streak",
          icon: <Flame className="h-5 w-5" />,
          unlocked: currentStreak >= 7,
          progress: Math.min(currentStreak, 7),
          target: 7,
          tier: "gold",
        },
        {
          id: "mrr_starter",
          title: "MRR Starter",
          description: `Reach ${currencySymbol}5,000 monthly recurring`,
          icon: <Star className="h-5 w-5" />,
          unlocked: mrr >= 5000,
          progress: Math.min(mrr, 5000),
          target: 5000,
          tier: "bronze",
        },
      ];
    }

    // Merchant-specific achievements (original)
    // "First Split" unlocks only when an order is fully paid AND shipped/delivered
    const hasFullyPaidAndShipped = requests.some(r => 
      r.status === "completed" && 
      (r.fulfillment_status === "shipped" || r.fulfillment_status === "delivered" || r.fulfillment_status === "out_for_delivery")
    );
    return [
      {
        id: "first_split",
        title: "First Split",
        description: "Fully paid & shipped split order",
        icon: <Rocket className="h-5 w-5" />,
        unlocked: hasFullyPaidAndShipped,
        progress: Math.min(hasFullyPaidAndShipped ? 1 : 0, 1),
        target: 1,
        tier: "bronze",
      },
      {
        id: "split_starter",
        title: "Split Starter",
        description: "Complete 10 split payments",
        icon: <Target className="h-5 w-5" />,
        unlocked: completedRequests >= 10,
        progress: Math.min(completedRequests, 10),
        target: 10,
        tier: "bronze",
      },
      {
        id: "split_master",
        title: "Split Master",
        description: "Complete 50 split payments",
        icon: <Trophy className="h-5 w-5" />,
        unlocked: completedRequests >= 50,
        progress: Math.min(completedRequests, 50),
        target: 50,
        tier: "silver",
      },
      {
        id: "revenue_milestone",
        title: "$1K Club",
        description: "Generate $1,000 in revenue",
        icon: <DollarSign className="h-5 w-5" />,
        unlocked: totalRevenue >= 1000,
        progress: Math.min(totalRevenue, 1000),
        target: 1000,
        tier: "gold",
      },
      {
        id: "revenue_king",
        title: "Revenue King",
        description: "Generate $10,000 in revenue",
        icon: <Crown className="h-5 w-5" />,
        unlocked: totalRevenue >= 10000,
        progress: Math.min(totalRevenue, 10000),
        target: 10000,
        tier: "platinum",
      },
      {
        id: "conversion_pro",
        title: "Conversion Pro",
        description: "Achieve 80%+ conversion rate",
        icon: <Zap className="h-5 w-5" />,
        unlocked: conversionRate >= 80 && totalRequests >= 5,
        progress: Math.min(conversionRate, 80),
        target: 80,
        tier: "silver",
      },
      {
        id: "streak_warrior",
        title: "Streak Warrior",
        description: "7-day payment streak",
        icon: <Flame className="h-5 w-5" />,
        unlocked: currentStreak >= 7,
        progress: Math.min(currentStreak, 7),
        target: 7,
        tier: "gold",
      },
      {
        id: "perfect_week",
        title: "Perfect Week",
        description: "Create 7 splits in a week",
        icon: <Star className="h-5 w-5" />,
        unlocked: splitsThisMonth >= 7,
        progress: Math.min(splitsThisMonth, 7),
        target: 7,
        tier: "bronze",
      },
    ];
  }, [analytics, splitsThisMonth, isProfessional]);

  const unlockedCount = achievements.filter(a => a.unlocked).length;
  const nextAchievement = achievements.find(a => !a.unlocked);

  // Chart configs
  const revenueChartConfig = {
    revenue: { label: "Revenue", color: "hsl(var(--primary))" },
    splits: { label: "Splits", color: "hsl(var(--muted-foreground))" },
  };

  const productChartConfig = {
    revenue: { label: "Revenue", color: "hsl(var(--primary))" },
  };

  // Export to CSV
  const exportToCSV = () => {
    try {
      const dateRangeStr = `${format(dateRange.from, "yyyy-MM-dd")}_to_${format(dateRange.to, "yyyy-MM-dd")}`;
      
      // Summary data
      let csvContent = "SHAKATAH ANALYTICS REPORT\n";
      csvContent += `Date Range,${format(dateRange.from, "MMM d, yyyy")} - ${format(dateRange.to, "MMM d, yyyy")}\n`;
      csvContent += `Generated On,${format(new Date(), "MMM d, yyyy HH:mm")}\n\n`;
      
      // Key Metrics
      csvContent += "KEY METRICS\n";
      csvContent += "Metric,Value\n";
      csvContent += `Total Revenue,${analytics.totalRevenue}\n`;
      csvContent += `Conversion Rate,${analytics.conversionRate.toFixed(1)}%\n`;
      csvContent += `Completed Splits,${analytics.completedRequests}\n`;
      csvContent += `Pending Splits,${analytics.pendingRequests}\n`;
      csvContent += `Expired Splits,${analytics.expiredRequests}\n`;
      csvContent += `Total Participants,${analytics.totalParticipants}\n`;
      csvContent += `Paid Participants,${analytics.paidParticipants}\n`;
      csvContent += `Average Split Value,${analytics.avgSplitValue}\n`;
      csvContent += `Pending Collection,${analytics.pendingRevenue}\n`;
      csvContent += `Success Rate,${analytics.requestConversionRate.toFixed(1)}%\n\n`;
      
      // Top Products
      csvContent += "TOP PERFORMING PRODUCTS\n";
      csvContent += "Product Name,Splits,Revenue,Conversion Rate\n";
      analytics.topProducts.forEach(product => {
        csvContent += `"${product.name}",${product.splits},${product.revenue},${product.conversionRate.toFixed(1)}%\n`;
      });
      csvContent += "\n";
      
      // Monthly Trend
      csvContent += "MONTHLY TREND\n";
      csvContent += "Month,Splits,Revenue\n";
      analytics.monthlyData.forEach(month => {
        csvContent += `${month.month},${month.splits},${month.revenue}\n`;
      });
      csvContent += "\n";
      
      // Individual Requests
      csvContent += "ALL SPLIT REQUESTS\n";
      csvContent += "Product Name,Total Price,Split Amount,Status,Created At,Participants,Paid Participants\n";
      filteredRequests.forEach(request => {
        const paidCount = request.participants.filter(p => p.has_paid).length;
        csvContent += `"${request.product_name}",${request.total_price},${request.split_amount},${request.status},${format(new Date(request.created_at), "yyyy-MM-dd")},${request.participants.length},${paidCount}\n`;
      });
      
      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `shakatah_analytics_${dateRangeStr}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      
      toast.success("CSV report downloaded successfully!");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export CSV report");
    }
  };

  // Export to XLSX
  const exportToXLSX = () => {
    try {
      const dateRangeStr = `${format(dateRange.from, "yyyy-MM-dd")}_to_${format(dateRange.to, "yyyy-MM-dd")}`;
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Summary Sheet
      const summaryData = [
        ["SHAKATAH ANALYTICS REPORT"],
        [`Date Range: ${format(dateRange.from, "MMM d, yyyy")} - ${format(dateRange.to, "MMM d, yyyy")}`],
        [`Generated: ${format(new Date(), "MMM d, yyyy HH:mm")}`],
        [],
        ["KEY METRICS"],
        ["Metric", "Value"],
        ["Total Revenue", analytics.totalRevenue],
        ["Conversion Rate", `${analytics.conversionRate.toFixed(1)}%`],
        ["Completed Splits", analytics.completedRequests],
        ["Pending Splits", analytics.pendingRequests],
        ["Expired Splits", analytics.expiredRequests],
        ["Total Participants", analytics.totalParticipants],
        ["Paid Participants", analytics.paidParticipants],
        ["Average Split Value", analytics.avgSplitValue],
        ["Pending Collection", analytics.pendingRevenue],
        ["Success Rate", `${analytics.requestConversionRate.toFixed(1)}%`],
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");
      
      // Top Products Sheet
      const productsData = [
        ["TOP PERFORMING PRODUCTS"],
        ["Product Name", "Splits", "Revenue", "Conversion Rate"],
        ...analytics.topProducts.map(product => [
          product.name,
          product.splits,
          product.revenue,
          `${product.conversionRate.toFixed(1)}%`,
        ]),
      ];
      const productsSheet = XLSX.utils.aoa_to_sheet(productsData);
      XLSX.utils.book_append_sheet(wb, productsSheet, "Top Products");
      
      // Monthly Trend Sheet
      const trendData = [
        ["MONTHLY TREND"],
        ["Month", "Splits", "Revenue"],
        ...analytics.monthlyData.map(month => [
          month.month,
          month.splits,
          month.revenue,
        ]),
      ];
      const trendSheet = XLSX.utils.aoa_to_sheet(trendData);
      XLSX.utils.book_append_sheet(wb, trendSheet, "Monthly Trend");
      
      // All Requests Sheet
      const requestsData = [
        ["ALL SPLIT REQUESTS"],
        ["Product Name", "Total Price", "Split Amount", "Status", "Created At", "Participants", "Paid Participants"],
        ...filteredRequests.map(request => {
          const paidCount = request.participants.filter(p => p.has_paid).length;
          return [
            request.product_name,
            request.total_price,
            request.split_amount,
            request.status,
            format(new Date(request.created_at), "yyyy-MM-dd"),
            request.participants.length,
            paidCount,
          ];
        }),
      ];
      const requestsSheet = XLSX.utils.aoa_to_sheet(requestsData);
      XLSX.utils.book_append_sheet(wb, requestsSheet, "All Requests");
      
      // Download file
      XLSX.writeFile(wb, `shakatah_analytics_${dateRangeStr}.xlsx`);
      
      toast.success("Excel report downloaded successfully!");
    } catch (error) {
      console.error("Error exporting XLSX:", error);
      toast.error("Failed to export Excel report");
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      const dateRangeStr = `${format(dateRange.from, "yyyy-MM-dd")}_to_${format(dateRange.to, "yyyy-MM-dd")}`;
      
      // Title
      doc.setFontSize(20);
      doc.setTextColor(139, 92, 246); // Primary purple color
      doc.text("Shakatah Analytics Report", 14, 20);
      
      // Date range
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Date Range: ${format(dateRange.from, "MMM d, yyyy")} - ${format(dateRange.to, "MMM d, yyyy")}`, 14, 28);
      doc.text(`Generated: ${format(new Date(), "MMM d, yyyy HH:mm")}`, 14, 33);
      
      // Key Metrics Section
      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text("Key Metrics", 14, 45);
      
      autoTable(doc, {
        startY: 50,
        head: [["Metric", "Value"]],
        body: [
          ["Total Revenue", formatCurrency(analytics.totalRevenue, currency)],
          ["Conversion Rate", `${analytics.conversionRate.toFixed(1)}%`],
          ["Completed Orders", analytics.completedRequests.toString()],
          ["Pending Orders", analytics.pendingRequests.toString()],
          ["Expired Orders", analytics.expiredRequests.toString()],
          ["Total Participants", analytics.totalParticipants.toString()],
          ["Paid Participants", analytics.paidParticipants.toString()],
          ["Average Split Value", formatCurrency(analytics.avgSplitValue, currency)],
          ["Pending Collection", formatCurrency(analytics.pendingRevenue, currency)],
          ["Success Rate", `${analytics.requestConversionRate.toFixed(1)}%`],
        ],
        theme: "striped",
        headStyles: { fillColor: [139, 92, 246] },
        styles: { fontSize: 9 },
      });
      
      // Top Products Section
      const metricsEndY = (doc as any).lastAutoTable.finalY + 10;
      doc.setFontSize(14);
      doc.text("Top Performing Products", 14, metricsEndY);
      
      autoTable(doc, {
        startY: metricsEndY + 5,
        head: [["Product Name", "Splits", "Revenue", "Conversion Rate"]],
        body: analytics.topProducts.map(product => [
          product.name,
          product.splits.toString(),
          formatCurrency(product.revenue, currency),
          `${product.conversionRate.toFixed(1)}%`,
        ]),
        theme: "striped",
        headStyles: { fillColor: [139, 92, 246] },
        styles: { fontSize: 9 },
      });
      
      // Monthly Trend Section
      const productsEndY = (doc as any).lastAutoTable.finalY + 10;
      doc.setFontSize(14);
      doc.text("Monthly Trend", 14, productsEndY);
      
      autoTable(doc, {
        startY: productsEndY + 5,
        head: [["Month", "Splits", "Revenue"]],
        body: analytics.monthlyData.map(month => [
          month.month,
          month.splits.toString(),
          formatCurrency(month.revenue, currency),
        ]),
        theme: "striped",
        headStyles: { fillColor: [139, 92, 246] },
        styles: { fontSize: 9 },
      });
      
      // Add new page for detailed requests if needed
      if (filteredRequests.length > 0) {
        doc.addPage();
        doc.setFontSize(14);
        doc.text("Split Payment Orders", 14, 20);
        
        autoTable(doc, {
          startY: 25,
          head: [["Product", "Total Price", "Status", "Date", "Participants"]],
          body: filteredRequests.slice(0, 50).map(request => {
            const paidCount = request.participants.filter(p => p.has_paid).length;
            return [
              request.product_name.substring(0, 25) + (request.product_name.length > 25 ? "..." : ""),
              formatCurrency(request.total_price, currency),
              request.status,
              format(new Date(request.created_at), "MMM d, yyyy"),
              `${paidCount}/${request.participants.length}`,
            ];
          }),
          theme: "striped",
          headStyles: { fillColor: [139, 92, 246] },
          styles: { fontSize: 8 },
        });
        
        if (filteredRequests.length > 50) {
          const requestsEndY = (doc as any).lastAutoTable.finalY + 5;
          doc.setFontSize(8);
          doc.setTextColor(100);
          doc.text(`Showing first 50 of ${filteredRequests.length} requests. Export to CSV for complete data.`, 14, requestsEndY);
        }
      }
      
      // Footer on all pages
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
        doc.text("Powered by Shakatah", doc.internal.pageSize.getWidth() - 14, doc.internal.pageSize.getHeight() - 10, { align: "right" });
      }
      
      // Save PDF
      doc.save(`shakatah_analytics_${dateRangeStr}.pdf`);
      toast.success("PDF report downloaded successfully!");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF report");
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters & Actions Bar */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            {/* Left: Period & Product Filters */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Period Dropdown */}
              <div className="flex items-center gap-2">
                <CalendarRange className="h-4 w-4 text-muted-foreground" />
                <Select 
                  value={selectedPreset} 
                  onValueChange={(value: PresetRange) => handlePresetChange(value)}
                >
                  <SelectTrigger className="w-[160px] h-9">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent className="bg-background border shadow-lg z-50">
                    <SelectItem value="7d">Last 7 Days</SelectItem>
                    <SelectItem value="30d">Last 30 Days</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="last_month">Last Month</SelectItem>
                    <SelectItem value="this_quarter">This Quarter</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Custom Date Picker - only visible when custom is selected */}
              {selectedPreset === "custom" && (
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 gap-2"
                    >
                      <CalendarIcon className="h-4 w-4" />
                      <span className="text-sm">
                        {format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d, yyyy")}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-background border shadow-lg z-50" align="start">
                    <div className="flex flex-col sm:flex-row">
                      <div className="p-3 border-b sm:border-b-0 sm:border-r">
                        <p className="text-sm font-medium mb-2 text-muted-foreground">From</p>
                        <Calendar
                          mode="single"
                          selected={dateRange.from}
                          onSelect={(date) => handleCustomDateSelect(date, "from")}
                          disabled={(date) => date > new Date()}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </div>
                      <div className="p-3">
                        <p className="text-sm font-medium mb-2 text-muted-foreground">To</p>
                        <Calendar
                          mode="single"
                          selected={dateRange.to}
                          onSelect={(date) => handleCustomDateSelect(date, "to")}
                          disabled={(date) => date > new Date() || date < dateRange.from}
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              {/* Product Filter - only show dropdown if there are 2+ distinct products */}
              {productNames.length > 1 && (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-px bg-border" />
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                    <SelectTrigger className="w-[180px] h-9">
                      <SelectValue placeholder="All Products" />
                    </SelectTrigger>
                    <SelectContent className="bg-background border shadow-lg z-50">
                      <SelectItem value="all">All Products</SelectItem>
                      {productNames.map((name) => (
                        <SelectItem key={name} value={name}>
                          {name.length > 25 ? name.substring(0, 25) + "..." : name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedProduct !== "all" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedProduct("all")}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              )}

              {/* Results count badge */}
              <Badge variant="outline" className="text-xs h-6">
                {filteredRequests.length} {filteredRequests.length === 1 ? labels.split : labels.splits}
              </Badge>
            </div>

            {/* Right: Compare Toggle & Download */}
            <div className="flex items-center gap-3">
              {/* Comparison Toggle */}
              <div className="flex items-center space-x-2">
                <Switch
                  id="comparison-mode"
                  checked={showComparison}
                  onCheckedChange={setShowComparison}
                />
                <Label htmlFor="comparison-mode" className="text-sm flex items-center gap-1.5 cursor-pointer">
                  <GitCompare className="h-4 w-4" />
                  <span className="hidden sm:inline">Compare</span>
                </Label>
              </div>
              {showComparison && (
                <Badge variant="secondary" className="text-xs">
                  vs {format(previousPeriod.from, "MMM d")} - {format(previousPeriod.to, "MMM d")}
                </Badge>
              )}

              {/* Download Dropdown */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 gap-2">
                    <Download className="h-4 w-4" />
                    <span className="hidden sm:inline">Download</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-44 p-1 bg-background border shadow-lg z-50" align="end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={exportToPDF}
                    className="w-full justify-start gap-2 h-9"
                  >
                    <FileText className="h-4 w-4" />
                    Export as PDF
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={exportToXLSX}
                    className="w-full justify-start gap-2 h-9"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Export as Excel
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={exportToCSV}
                    className="w-full justify-start gap-2 h-9"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Export as CSV
                  </Button>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Summary Stats - Avg Split Value, Pending Collection, Total Participants, Success Rate */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">{labels.avgSplitValue}</p>
              <p className="text-2xl font-bold">{formatCurrency(analytics.avgSplitValue, currency)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Pending Collection</p>
              <p className="text-2xl font-bold text-yellow-600">{formatCurrency(analytics.pendingRevenue, currency)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">{isProfessional ? "Total Clients" : "Total Participants"}</p>
              <p className="text-2xl font-bold">{analytics.totalParticipants}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">{isProfessional ? "Collection Rate" : "Success Rate"}</p>
              <p className="text-2xl font-bold text-green-600">{analytics.requestConversionRate.toFixed(0)}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hero Stats with Gamification */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Revenue */}
        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">{labels.revenue}</p>
                <p className="text-3xl font-bold tracking-tight">{formatCurrency(analytics.collectedRevenue, currency)}</p>
                {showComparison && previousMetrics ? (
                  (() => {
                    const change = getChangePercent(analytics.totalRevenue, previousMetrics.totalRevenue);
                    return (
                      <div className="flex items-center gap-1 mt-1">
                        {change.isPositive ? (
                          <ArrowUpRight className="h-4 w-4 text-green-500" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4 text-red-500" />
                        )}
                        <span className={`text-sm font-medium ${change.isPositive ? "text-green-500" : "text-red-500"}`}>
                          {change.value.toFixed(1)}% vs prev
                        </span>
                      </div>
                    );
                  })()
                ) : (
                  <div className="flex items-center gap-1 mt-1">
                    <ArrowUpRight className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-500 font-medium">{labels.revenueVia}</span>
                  </div>
                )}
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <DollarSign className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Conversion Rate */}
        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-green-500/10" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">{labels.conversionRate}</p>
                <p className="text-3xl font-bold tracking-tight">{analytics.conversionRate.toFixed(1)}%</p>
                {showComparison && previousMetrics ? (
                  (() => {
                    const change = getChangePercent(analytics.conversionRate, previousMetrics.conversionRate);
                    return (
                      <div className="flex items-center gap-1 mt-1">
                        {change.isPositive ? (
                          <ArrowUpRight className="h-4 w-4 text-green-500" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4 text-red-500" />
                        )}
                        <span className={`text-sm font-medium ${change.isPositive ? "text-green-500" : "text-red-500"}`}>
                          {change.value.toFixed(1)}pp vs prev
                        </span>
                      </div>
                    );
                  })()
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">
                    {analytics.paidParticipants}/{analytics.totalParticipants} {labels.participants}
                  </p>
                )}
              </div>
              <div className="p-3 bg-green-500/10 rounded-full">
                <TrendingUp className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Completed Splits */}
        <Card className="relative overflow-hidden">
          <div className={`absolute inset-0 bg-gradient-to-br ${isProfessional ? 'from-cyan-500/5 to-cyan-500/10' : 'from-blue-500/5 to-blue-500/10'}`} />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">{labels.completedSplits}</p>
                <p className="text-3xl font-bold tracking-tight">{analytics.completedRequests}</p>
                {showComparison && previousMetrics ? (
                  (() => {
                    const change = getChangePercent(analytics.completedRequests, previousMetrics.completedRequests);
                    return (
                      <div className="flex items-center gap-1 mt-1">
                        {change.isPositive ? (
                          <ArrowUpRight className="h-4 w-4 text-green-500" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4 text-red-500" />
                        )}
                        <span className={`text-sm font-medium ${change.isPositive ? "text-green-500" : "text-red-500"}`}>
                          {change.value.toFixed(1)}% vs prev
                        </span>
                      </div>
                    );
                  })()
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">
                    of {analytics.totalRequests} total
                  </p>
                )}
              </div>
              <div className={`p-3 ${isProfessional ? 'bg-cyan-500/10' : 'bg-blue-500/10'} rounded-full`}>
                <CheckCircle2 className={`h-6 w-6 ${isProfessional ? 'text-cyan-500' : 'text-blue-500'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Current Streak */}
        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-orange-500/10" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">{labels.paymentStreak}</p>
                <div className="flex items-center gap-2">
                  <p className="text-3xl font-bold tracking-tight">{analytics.currentStreak}</p>
                  <Flame className={`h-6 w-6 ${analytics.currentStreak > 0 ? "text-orange-500 animate-pulse" : "text-muted-foreground"}`} />
                </div>
                <p className="text-sm text-muted-foreground mt-1">consecutive days</p>
              </div>
              <div className="p-3 bg-orange-500/10 rounded-full">
                <Sparkles className="h-6 w-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Professional KPIs Section - Only for professionals */}
      {isProfessional && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Monthly Recurring Revenue */}
          <Card className="relative overflow-hidden border-l-4 border-l-cyan-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Monthly Revenue (MRR)</p>
                  <p className="text-2xl font-bold tracking-tight">{formatCurrency(analytics.mrr, currency)}</p>
                  <p className="text-xs text-muted-foreground mt-1">This month's collections</p>
                </div>
                <div className="p-3 bg-cyan-500/10 rounded-full">
                  <TrendingUp className="h-5 w-5 text-cyan-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Client Retention Rate */}
          <Card className="relative overflow-hidden border-l-4 border-l-teal-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Client Retention</p>
                  <p className="text-2xl font-bold tracking-tight">{analytics.clientRetentionRate.toFixed(1)}%</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {analytics.repeatClients} of {analytics.totalClients} repeat clients
                  </p>
                </div>
                <div className="p-3 bg-teal-500/10 rounded-full">
                  <Target className="h-5 w-5 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Average Payment Delay */}
          <Card className="relative overflow-hidden border-l-4 border-l-emerald-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Avg. Payment Time</p>
                  <p className="text-2xl font-bold tracking-tight">
                    {analytics.avgPaymentDelay < 1 
                      ? "< 1 day" 
                      : `${analytics.avgPaymentDelay.toFixed(1)} days`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {analytics.avgPaymentDelay <= 3 
                      ? "Great collection speed!" 
                      : analytics.avgPaymentDelay <= 7 
                        ? "Good payment cycle" 
                        : "Consider reminders"}
                  </p>
                </div>
                <div className="p-3 bg-emerald-500/10 rounded-full">
                  <Zap className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Achievements Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Achievements
              </CardTitle>
              <CardDescription>
                {unlockedCount}/{achievements.length} unlocked
              </CardDescription>
            </div>
            {nextAchievement && (
              <Badge variant="outline" className="flex items-center gap-1">
                <Target className="h-3 w-3" />
                Next: {nextAchievement.title}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {achievements.map((achievement) => (
              <div
                key={achievement.id}
                className={`relative p-4 rounded-lg border-2 transition-all ${
                  achievement.unlocked
                    ? `${TIER_BG[achievement.tier]} shadow-lg`
                    : "bg-muted/30 border-muted opacity-60"
                }`}
              >
                {achievement.unlocked && (
                  <div className={`absolute -top-2 -right-2 p-1 rounded-full bg-gradient-to-r ${TIER_COLORS[achievement.tier]}`}>
                    <Medal className="h-4 w-4 text-white" />
                  </div>
                )}
                <div className="flex flex-col items-center text-center gap-2">
                  <div className={`p-2 rounded-full ${achievement.unlocked ? `bg-gradient-to-r ${TIER_COLORS[achievement.tier]}` : "bg-muted"}`}>
                    <span className={achievement.unlocked ? "text-white" : "text-muted-foreground"}>
                      {achievement.icon}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{achievement.title}</p>
                    <p className="text-xs text-muted-foreground">{achievement.description}</p>
                  </div>
                  {!achievement.unlocked && (
                    <div className="w-full">
                      <Progress 
                        value={(achievement.progress / achievement.target) * 100} 
                        className="h-1.5"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {achievement.id === "revenue_milestone" || achievement.id === "revenue_king"
                          ? `${formatCurrency(achievement.progress, currency)} / ${formatCurrency(achievement.target, currency)}`
                          : achievement.id === "conversion_pro"
                          ? `${achievement.progress.toFixed(0)}% / ${achievement.target}%`
                          : `${achievement.progress} / ${achievement.target}`
                        }
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{isProfessional ? "Earnings Trend" : "Revenue Trend"}</CardTitle>
            <CardDescription>Last 6 months performance</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={revenueChartConfig} className="h-[250px] w-full">
              <AreaChart data={analytics.monthlyData}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} tickFormatter={(value) => {
                  if (value === 0) return `${getCurrencySymbol(currency)}0`;
                  if (value >= 1000) return `${getCurrencySymbol(currency)}${(value / 1000).toFixed(0)}k`;
                  return `${getCurrencySymbol(currency)}${value}`;
                }} />
                <ChartTooltip content={<ChartTooltipContent formatter={(value) => formatCurrency(Number(value), currency)} />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="hsl(var(--primary))"
                  fill="url(#revenueGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Split Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{labels.splitStatus}</CardTitle>
            <CardDescription>
              {labels.splitStatusDesc}
              {onNavigateToOrders && analytics.statusData.length > 0 && (
                <span className="text-xs ml-2">(Click to filter)</span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-center">
            {analytics.statusData.length > 0 ? (
              <div className="flex items-center gap-8">
                <div className="h-[200px] w-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analytics.statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {analytics.statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-3">
                  {analytics.statusData.map((item, index) => (
                    <button
                      key={item.name}
                      className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer text-left"
                      onClick={() => onNavigateToOrders?.(item.name.toLowerCase())}
                      title={`View ${item.name.toLowerCase()} ${labels.splits}`}
                    >
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                      />
                      <span className="text-sm font-medium">{item.name}</span>
                      <span className="text-sm text-muted-foreground">({item.value})</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                {isProfessional ? (
                  <DollarSign className="h-12 w-12 mb-2 opacity-50" />
                ) : (
                  <Gift className="h-12 w-12 mb-2 opacity-50" />
                )}
                <p>{labels.noSplits}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Products */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isProfessional ? (
              <DollarSign className="h-5 w-5" />
            ) : (
              <ShoppingBag className="h-5 w-5" />
            )}
            {labels.topProducts}
          </CardTitle>
          <CardDescription>{labels.topProductsDesc}</CardDescription>
        </CardHeader>
        <CardContent>
          {analytics.topProducts.length > 0 ? (
            <ChartContainer config={productChartConfig} className="h-[300px] w-full">
              <BarChart data={analytics.topProducts} layout="vertical">
                <XAxis type="number" tickFormatter={(value) => {
                  if (value === 0) return `${getCurrencySymbol(currency)}0`;
                  if (value >= 1000) return `${getCurrencySymbol(currency)}${(value / 1000).toFixed(0)}k`;
                  return `${getCurrencySymbol(currency)}${value}`;
                }} />
                <YAxis type="category" dataKey="name" width={120} tickLine={false} axisLine={false} />
                <ChartTooltip 
                  content={
                    <ChartTooltipContent 
                      formatter={(value, name, props) => {
                        if (name === "revenue") return formatCurrency(Number(value), currency);
                        return value;
                      }}
                    />
                  } 
                />
                <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ChartContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
              {isProfessional ? (
                <DollarSign className="h-12 w-12 mb-2 opacity-50" />
              ) : (
                <ShoppingBag className="h-12 w-12 mb-2 opacity-50" />
              )}
              <p>{labels.noProductData}</p>
              <p className="text-sm">{labels.startCreating}</p>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
